import { useState, useEffect, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { useMsal } from '@azure/msal-react';
import { config } from '../config';

export interface ConnectedUserInfo {
  connectionId: string;
  userId: string;
  username: string;
  displayName: string;
  emailAddress: string;
  roleId: number;
  clientType: string; // "web" | "mobile"
  connectedAt: string; // ISO string
}

export interface AdminMessage {
  fromConnectionId: string;
  fromUserId: string;
  fromDisplayName: string;
  fromEmailAddress: string;
  toUserId: string;
  toDisplayName: string;
  message: string;
  sentAt: string; // ISO string
}

export interface UsePresenceReturn {
  connectedUsers: ConnectedUserInfo[];
  isConnected: boolean;
  error: string | null;
  messages: AdminMessage[];
  sendMessage: (targetUserId: string, message: string) => Promise<void>;
}

export type OnMessageCallback = (msg: AdminMessage) => void;

const INITIAL_RETRY_DELAY_MS = 2000;
const MAX_RETRY_DELAY_MS = 30000;
const MAX_INITIAL_RETRIES = 8;

/**
 * Connects to the SignalR Presence hub and tracks online users.
 * Only super-admins (roleId === 1) receive the connected-user list from the server.
 * Pass `enabled = false` to skip connecting (e.g. while the current user role is loading).
 * Pass `onMessage` to receive new admin messages immediately without polling `messages` state.
 */
export const usePresence = (enabled: boolean = true, onMessage?: OnMessageCallback): UsePresenceReturn => {
  const [connectedUsers, setConnectedUsers] = useState<ConnectedUserInfo[]>([]);
  const [messages, setMessages] = useState<AdminMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);
  const stoppedRef = useRef(false);

  const { instance, accounts } = useMsal();

  // Keep the onMessage callback in a ref so the SignalR handler always calls the
  // latest version without needing to rebuild the connection when it changes.
  const onMessageRef = useRef<OnMessageCallback | undefined>(onMessage);
  // Sync ref on every render without triggering effects.
  onMessageRef.current = onMessage;

  // Acquire the freshest possible access token from MSAL.
  // Falls back to localStorage if MSAL silent acquisition fails so that the
  // hub connection attempt still proceeds (the server-side sub→email cache
  // handles the case where the refreshed token lacks B2C user claims).
  const getToken = useCallback(async (): Promise<string> => {
    try {
      const account = accounts[0];
      if (account) {
        const response = await instance.acquireTokenSilent({
          ...config.tokenRequest,
          account,
        });
        // Keep localStorage in sync so other consumers (Apollo) stay current.
        localStorage.setItem('msal.token', response.accessToken);
        return response.accessToken;
      }
    } catch {
      // Silent acquisition failed; fall through to localStorage fallback.
    }
    return localStorage.getItem('msal.token') ?? '';
  }, [instance, accounts]);

  useEffect(() => {
    if (!enabled) {
      console.log('[Presence] disabled, not connecting.');
      return;
    }

    const token = localStorage.getItem('msal.token');
    console.log('[Presence] enabled=true, token present:', Boolean(token));

    stoppedRef.current = false;
    retryCountRef.current = 0;

    const hubUrl = `${config.apiBaseUrl}/hubs/presence?clientType=web`;
    console.log('[Presence] Connecting to:', hubUrl);

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: getToken,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 15000, 30000])
      .configureLogging(signalR.LogLevel.Debug)
      .build();

    connectionRef.current = connection;

    // Received when super-admin first connects – full snapshot
    connection.on('ConnectedUsers', (users: ConnectedUserInfo[]) => {
      setConnectedUsers(users);
    });

    // Received whenever any user connects
    connection.on('UserConnected', (user: ConnectedUserInfo) => {
      setConnectedUsers(prev => {
        // Replace any existing entry for this userId (handles re-connections and StrictMode double-mount)
        const filtered = prev.filter(u => u.userId !== user.userId && u.connectionId !== user.connectionId);
        return [...filtered, user];
      });
    });

    // Received whenever any user disconnects
    connection.on('UserDisconnected', (data: { connectionId: string }) => {
      setConnectedUsers(prev => prev.filter(u => u.connectionId !== data.connectionId));
    });

    // Admin message received (sent by a super-admin to this connection)
    connection.on('ReceiveMessage', (msg: AdminMessage) => {
      console.log('[Presence] ReceiveMessage event fired:', msg);
      // Fire the callback immediately – before any state update – so consumers
      // like the Snackbar in AdminLayout don't depend on the effect re-run cycle.
      onMessageRef.current?.(msg);
      setMessages(prev => [...prev, msg]);
    });

    connection.onreconnecting(() => setIsConnected(false));
    connection.onreconnected(() => {
      setIsConnected(true);
      connection.invoke('GetConnectedUsers').catch(() => { /* non-super-admin – ignore */ });
    });
    connection.onclose(() => {
      setIsConnected(false);
      // Schedule a retry if the connection closed unexpectedly (not via cleanup)
      if (!stoppedRef.current && retryCountRef.current < MAX_INITIAL_RETRIES) {
        scheduleRetry(connection);
      }
    });

    const scheduleRetry = (conn: signalR.HubConnection) => {
      if (stoppedRef.current) return;
      retryCountRef.current += 1;
      const delay = Math.min(
        INITIAL_RETRY_DELAY_MS * Math.pow(1.5, retryCountRef.current - 1),
        MAX_RETRY_DELAY_MS
      );
      console.log(`[Presence] Retrying in ${Math.round(delay / 1000)}s (attempt ${retryCountRef.current}/${MAX_INITIAL_RETRIES})`); 
      retryTimerRef.current = setTimeout(() => attemptStart(conn), delay);
    };

    const attemptStart = async (conn: signalR.HubConnection) => {
      if (stoppedRef.current) return;
      try {
        await conn.start();
        retryCountRef.current = 0;
        setIsConnected(true);
        setError(null);
        console.log('[Presence] ✅ Connected to SignalR presence hub');
      } catch (err: any) {
        const msg = err?.message ?? 'Failed to connect to presence hub';
        setError(msg);
        setIsConnected(false);
        console.log('[Presence] ❌ Connection failed:', msg);
        if (retryCountRef.current < MAX_INITIAL_RETRIES) {
          scheduleRetry(conn);
        } else {
          setError('Could not connect to presence hub after multiple attempts. Refresh to retry.');
        }
      }
    };

    attemptStart(connection);

    return () => {
      stoppedRef.current = true;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      connection.stop();
    };
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = useCallback(async (targetUserId: string, message: string): Promise<void> => {
    const conn = connectionRef.current;
    if (!conn || conn.state !== signalR.HubConnectionState.Connected) {
      throw new Error('Not connected to presence hub');
    }
    await conn.invoke('SendMessageToUser', targetUserId, message);
  }, []);

  return { connectedUsers, isConnected, error, messages, sendMessage };
};
